import { type TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';

import { AveragePlayerScore } from '@sorare/core/src/__generated__/globalTypes';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { HeadlineL } from '@sorare/core/src/atoms/typography';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { playerDetails } from '@sorare/core/src/lib/glossary';

import { AveragePlayerScores } from '@sorare/marketplace/src/components/player/AveragePlayerScores';
import { PerformanceBlocks } from '@sorare/marketplace/src/components/player/PerformanceBlocks';
import { FilterKey } from '@sorare/marketplace/src/lib/performance';

import { LazyFixtureChart } from 'components/stats/LastScores/LazyFixtureChart';
import { usePerformanceFilters } from 'hooks/usePerformanceFilters';
import { usePerformanceFiltersOptions } from 'hooks/usePerformanceFiltersOptions';

import { AveragePlayerScoreSwitch } from './AveragePlayerScoreSwitch';
import type {
  PerformanceBlocksQuery,
  PerformanceBlocksQueryVariables,
  Performance_anyPlayer,
} from './__generated__/index.graphql';

type Props = {
  player: Performance_anyPlayer;
};

const PERFORMANCE_BLOCK_QUERY = gql`
  query PerformanceBlocksQuery(
    $playerSlug: String!
    $position: Position!
    $competitionSlug: String
    $location: AverageLastScoreLocation
    $span: AverageLastScoreSpan!
    $teamSlug: String
  ) {
    anyPlayer(slug: $playerSlug) {
      slug
      ...PerformanceBlocks_anyPlayer
      ...AveragePlayerScores_anyPlayer
    }
  }
  ${AveragePlayerScores.fragments.anyPlayer}
  ${PerformanceBlocks.fragments.anyPlayer}
` as TypedDocumentNode<PerformanceBlocksQuery, PerformanceBlocksQueryVariables>;

export const Performance = ({ player }: Props) => {
  const {
    flags: { useFilterOptionsFromMembership = false },
  } = useFeatureFlags();
  const { filters, handleFilterChange, gqlFilters } = usePerformanceFilters();
  const options = usePerformanceFiltersOptions({
    player,
  });

  const position = player.anyPositions[0];
  const { data, loading } = useQuery(PERFORMANCE_BLOCK_QUERY, {
    variables: {
      playerSlug: player.slug,
      position,
      competitionSlug: gqlFilters.competitionSlug,
      span: gqlFilters.span,
      teamSlug: gqlFilters.teamSlug,
      location: gqlFilters.location,
    },
  });

  const { anyPlayer } = data || {};

  return (
    <Vertical gap={2}>
      <AveragePlayerScores
        player={player}
        modes={[
          AveragePlayerScore.LAST_FIVE_SO5_AVERAGE_SCORE,
          AveragePlayerScore.LAST_FIFTEEN_SO5_AVERAGE_SCORE,
          AveragePlayerScore.LAST_FORTY_SO5_AVERAGE_SCORE,
        ]}
      />
      <HeadlineL as="h2">
        <FormattedMessage {...playerDetails.breakdown} />
      </HeadlineL>
      <LazyFixtureChart
        playerSlug={player.slug}
        filters={filters}
        onFilterChange={handleFilterChange}
        {...(useFilterOptionsFromMembership && { options })}
        TitleComponent={() => null}
        stickyFilters
      />
      <AveragePlayerScoreSwitch
        averagePlayerScore={filters[FilterKey.MODE]}
        onChange={value => handleFilterChange(FilterKey.MODE, value)}
      />
      <PerformanceBlocks
        player={anyPlayer}
        filters={filters}
        loading={loading}
      />
    </Vertical>
  );
};

Performance.fragments = {
  anyPlayer: gql`
    fragment Performance_anyPlayer on AnyPlayerInterface {
      slug
      anyPositions
      ...AveragePlayerScores_anyPlayer
      ...usePerformanceFiltersOptions_anyPlayer
    }
    ${usePerformanceFiltersOptions.fragments.anyPlayer}
    ${AveragePlayerScores.fragments.anyPlayer}
  ` as TypedDocumentNode<Performance_anyPlayer>,
};
